/**
 * Stego Header v2 — 77-byte binary format.
 *
 * Layout (big-endian, no padding):
 *   0x00  4   Magic: 0xEF 0xC0 0xDE 0x42
 *   0x04  1   Version (0x02)
 *   0x05  1   Technique (0x01=metadata, 0x02=lsb, 0x04=polyglot, 0x05=cover_art)
 *   0x06  1   Flags: bit0=chunked, bit1=is_zip, bit2=is_file
 *   0x07  1   Chunk index (0-based)
 *   0x08  1   Total chunks
 *   0x09  4   Original payload size (uint32 BE)
 *   0x0D  4   Chunk/compressed size (uint32 BE)
 *   0x11  32  Payload SHA-256
 *   0x31  12  AES-GCM nonce
 *   0x3D  16  AES-GCM auth tag
 *
 * Python struct: ">4sBBBBBI I 32s 12s 16s"
 */

export const MAGIC = new Uint8Array([0xef, 0xc0, 0xde, 0x42]);
export const HEADER_VERSION = 0x02;
export const HEADER_SIZE = 77;

export const enum Technique {
  METADATA = 0x01,
  LSB = 0x02,
  MDCT = 0x03,
  POLYGLOT = 0x04,
  COVER_ART = 0x05,
}

export interface StegoHeader {
  version: number;
  technique: Technique;
  isChunked: boolean;
  isZip: boolean;
  isFile: boolean;
  chunkIndex: number;
  totalChunks: number;
  originalSize: number;
  chunkSize: number;
  payloadSha256: Uint8Array; // 32 bytes
  nonce: Uint8Array; // 12 bytes
  authTag: Uint8Array; // 16 bytes
}

/** Pack header into 77-byte ArrayBuffer (big-endian). */
export function packHeader(h: StegoHeader): Uint8Array {
  const buf = new ArrayBuffer(HEADER_SIZE);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  // Magic (4 bytes)
  bytes.set(MAGIC, 0);

  // Version, technique (1 byte each)
  view.setUint8(4, h.version);
  view.setUint8(5, h.technique);

  // Flags
  const flags =
    (h.isChunked ? 1 : 0) | ((h.isZip ? 1 : 0) << 1) | ((h.isFile ? 1 : 0) << 2);
  view.setUint8(6, flags);

  // Chunk index, total chunks
  view.setUint8(7, h.chunkIndex);
  view.setUint8(8, h.totalChunks);

  // Original size, chunk size (uint32 BE)
  view.setUint32(9, h.originalSize, false);
  view.setUint32(13, h.chunkSize, false);

  // SHA-256 (32 bytes at offset 0x11 = 17)
  bytes.set(h.payloadSha256.slice(0, 32), 17);

  // Nonce (12 bytes at offset 0x31 = 49)
  bytes.set(h.nonce.slice(0, 12), 49);

  // Auth tag (16 bytes at offset 0x3D = 61)
  bytes.set(h.authTag.slice(0, 16), 61);

  return bytes;
}

/** Unpack header from binary data. Returns null if magic doesn't match. */
export function unpackHeader(data: Uint8Array): StegoHeader | null {
  if (data.length < HEADER_SIZE) return null;

  // Check magic
  if (
    data[0] !== 0xef ||
    data[1] !== 0xc0 ||
    data[2] !== 0xde ||
    data[3] !== 0x42
  ) {
    return null;
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  const flags = view.getUint8(6);

  return {
    version: view.getUint8(4),
    technique: view.getUint8(5) as Technique,
    isChunked: (flags & 1) !== 0,
    isZip: (flags & 2) !== 0,
    isFile: (flags & 4) !== 0,
    chunkIndex: view.getUint8(7),
    totalChunks: view.getUint8(8),
    originalSize: view.getUint32(9, false),
    chunkSize: view.getUint32(13, false),
    payloadSha256: data.slice(17, 49),
    nonce: data.slice(49, 61),
    authTag: data.slice(61, 77),
  };
}

export function techniqueName(t: Technique): string {
  switch (t) {
    case Technique.METADATA:
      return "metadata";
    case Technique.LSB:
      return "lsb";
    case Technique.MDCT:
      return "mdct";
    case Technique.POLYGLOT:
      return "polyglot";
    case Technique.COVER_ART:
      return "cover_art";
    default:
      return `unknown(0x${(t as number).toString(16)})`;
  }
}
